import { useCallback, useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';

export interface TimeoutTooltip {
  key: string;
  time?: number;
  dep?: React.DependencyList;
  isUnrevealed?: () => boolean;
  isRevealedWithScroll?: () => boolean;
  saveOnShow?: boolean;
}

export const useTimeoutTooltip = ({
  key,
  time,
  dep = [],
  isUnrevealed,
  isRevealedWithScroll,
  saveOnShow = true,
}: TimeoutTooltip) => {
  const [isTooltipShown, setIsTooltipShown] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showTooltip = useCallback(() => {
    setIsTooltipShown(true);

    if (saveOnShow) {
      localStorage.setItem(key, 'true');
    }

    if (time === undefined) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setIsTooltipShown(false);
    }, time);
  }, [key, saveOnShow, time]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    try {
      const tooltipStatus = localStorage.getItem(key);

      if (tooltipStatus === 'true' || isUnrevealed?.()) {
        return;
      }

      showTooltip();

      const onScroll = throttle(() => {
        if (isRevealedWithScroll?.() && !isTooltipShown) {
          showTooltip();
        }
      });

      if (isRevealedWithScroll) {
        window.addEventListener('scroll', onScroll);
        return () => {
          window.removeEventListener('scroll', onScroll);
        };
      }
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTooltipShown, showTooltip, ...dep]);

  /**
   * 툴팁을 닫고 localStorage에 저장
   * saveOnShow가 false인 경우 이 함수를 사용해야 다시 표시되지 않음
   */
  const hideTooltip = useCallback(() => {
    setIsTooltipShown(false);
    localStorage.setItem(key, 'true');
  }, [key]);

  return { isTooltipShown, setIsTooltipShown, hideTooltip };
};
